/**
 * errorReporter.js
 *
 * Sends unhandled JS errors to the backend /api/client-errors endpoint so they
 * appear in the same CloudWatch log group as backend errors.
 *
 * Only active in production — dev errors stay in the browser console.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5161';
const ENDPOINT = `${API_BASE_URL}/api/client-errors`;

// Deduplicate: avoid spamming the same error multiple times in a session
const _seen = new Set();

/**
 * @param {string} message
 * @param {string|null} [stack]
 * @param {string|null} [url]
 */
export function reportError(message, stack = null, url = null) {
  if (process.env.NODE_ENV !== 'production') return;

  // Dedup key: first 200 chars of message + url
  const key = `${String(message).slice(0, 200)}|${url || window.location.href}`;
  if (_seen.has(key)) return;
  _seen.add(key);

  const payload = {
    message: String(message).slice(0, 1000),
    stack: stack ? String(stack).slice(0, 2000) : null,
    url: url || window.location.href,
    userAgent: navigator.userAgent,
  };

  // Fire-and-forget — never throw or block the UI
  try {
    navigator.sendBeacon
      ? navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(payload)], { type: 'application/json' }))
      : fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
  } catch {
    // Silently swallow — logging must never cause further errors
  }
}

/**
 * Registers window-level handlers for uncaught errors and unhandled promise
 * rejections. Call once from index.js.
 */
export function installGlobalErrorHandlers() {
  if (process.env.NODE_ENV !== 'production') return;

  window.addEventListener('error', (event) => {
    const msg = event.error?.message || event.message || 'Unknown error';
    const stack = event.error?.stack || null;
    reportError(msg, stack, window.location.href);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = reason instanceof Error ? reason.message : String(reason ?? 'Unhandled promise rejection');
    const stack = reason instanceof Error ? reason.stack : null;
    reportError(msg, stack, window.location.href);
  });
}
